/**
 * Pure decision helper for the post-first-pick prompt flow.
 *
 * Mirrors the branching that runs after a World Cup pick is saved:
 *   - Anonymous (guest) user → "Don't lose your rank" sign-up prompt (once / session).
 *   - Signed-in user who has not yet seen the WC→Arena bridge → show bridge.
 *   - Signed-in user who has already seen the bridge OR already saw it this session
 *     → show nothing.
 *
 * Returning a flat object instead of mutating React state keeps the helper
 * trivially unit-testable; the caller is responsible for applying the decision.
 */

export interface PostFirstPickPromptInput {
  /** True only when auth is hydrated and the user is signed in (anon or real). */
  isLoaded: boolean;
  isSignedIn: boolean;
  isAnonymous: boolean;
  /** False until the helper layer has read AsyncStorage for the user's bridge flag. */
  hasSeenWcToArenaBridge: boolean;
  /** True while the guest prompt has already been opened in the current session. */
  guestPromptShownThisSession: boolean;
  /** True while the bridge prompt has already been opened in the current session. */
  bridgePromptShownThisSession: boolean;
}

export interface PostFirstPickPromptDecision {
  showGuestPrompt: boolean;
  showBridge: boolean;
}

const NO_PROMPT: PostFirstPickPromptDecision = {
  showGuestPrompt: false,
  showBridge: false,
};

export function decidePostFirstPickPrompt(
  input: PostFirstPickPromptInput,
): PostFirstPickPromptDecision {
  if (!input.isLoaded || !input.isSignedIn) {
    return NO_PROMPT;
  }
  if (input.isAnonymous) {
    if (input.guestPromptShownThisSession) {
      return NO_PROMPT;
    }
    return { showGuestPrompt: true, showBridge: false };
  }
  if (input.hasSeenWcToArenaBridge) {
    return NO_PROMPT;
  }
  if (input.bridgePromptShownThisSession) {
    return NO_PROMPT;
  }
  return { showGuestPrompt: false, showBridge: true };
}
